import React, { useState, useEffect, useCallback } from 'react'

// Imported Modular Components
import CallLogsHeader from './components/CallLogsHeader'
import CallLogsKpiCards from './components/CallLogsKpiCards'
import CallLogsTable from './components/CallLogsTable'
import CallAuditDetailDrawer from './components/CallAuditDetailDrawer'
import SharedFilters from '../../../components/SharedFilters'
import AppPagination from '../../../components/AppPagination'

// API
import { fetchCallLogs, fetchProvisionedAgents } from '../services/telephonyApi'

const SEARCH_FIELD_OPTIONS = [
  { value: 'All', label: 'All Fields' },
  { value: 'Number', label: 'Phone Number' },
  { value: 'Customer', label: 'Customer / Project' },
  { value: 'Agent', label: 'Agent Name' },
  { value: 'Notes', label: 'Call Notes' },
]

const STATUS_OPTIONS = [
  { value: 'All', label: 'All Statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'missed', label: 'Missed Call' },
  { value: 'busy', label: 'Agent Declined / Busy' },
  { value: 'canceled', label: 'Dropped / Abandoned' },
  { value: 'no-answer', label: 'No Answer' },
  { value: 'failed', label: 'Failed' },
]

const DIRECTION_OPTIONS = [
  { value: 'All', label: 'All Directions' },
  { value: 'INBOUND', label: 'Inbound' },
  { value: 'OUTBOUND', label: 'Outbound' },
]

/**
 * CallLogsScreen
 * Pure database-first call history powered by local CallLogs table.
 * Standardized with shared global SharedFilters and server-side AppPagination.
 * KPI summary cards left untouched per explicit user instruction.
 */
export default function CallLogsScreen() {
  // Filter States
  const [searchField, setSearchField] = useState('All')
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState('All')
  const [directionFilter, setDirectionFilter] = useState('All')
  const [projectFilter, setProjectFilter] = useState('All')
  const [agentFilter, setAgentFilter] = useState('All')
  const [agentOptions, setAgentOptions] = useState([{ value: 'All', label: 'All Users' }])
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  // Load provisioned telephony agents for filter dropdown
  useEffect(() => {
    async function loadAgents() {
      try {
        const rawAgents = await fetchProvisionedAgents()
        const items = Array.isArray(rawAgents) ? rawAgents : []
        const options = [
          { value: 'All', label: 'All Users' },
          ...items.map((a) => ({
            value: String(a.user_id),
            label: a.user_name ? `${a.user_name} (${a.user_email || 'Agent'})` : (a.user_email || `Agent #${a.user_id}`),
          })),
        ]
        setAgentOptions(options)
      } catch (err) {
        console.warn('Failed to fetch provisioned telephony agents:', err)
      }
    }
    loadAgents()
  }, [])

  // Data state
  const [callLogs, setCallLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const PAGE_SIZE = 20

  // Selected Log Drawer & Active Audio Player
  const [selectedLog, setSelectedLog] = useState(null)
  const [playingAudioId, setPlayingAudioId] = useState(null)

  const loadCallLogs = useCallback(async (pageNum = 1) => {
    setLoading(true)
    try {
      const res = await fetchCallLogs({
        page: pageNum,
        pageSize: PAGE_SIZE,
        search: searchText,
        searchField: searchField,
        direction: directionFilter,
        status: statusFilter,
        projectId: projectFilter,
        agentUserId: agentFilter,
        fromDate: fromDate,
        toDate: toDate,
      })
      const items = res?.data?.items || res?.items || res?.data || []
      const total = res?.data?.total ?? res?.total ?? items.length
      setCallLogs(items)
      setTotalCount(total)
    } catch (err) {
      console.error('Failed to load call logs:', err)
      setCallLogs([])
      setTotalCount(0)
    } finally {
      setLoading(false)
    }
  }, [searchText, searchField, directionFilter, statusFilter, projectFilter, agentFilter, fromDate, toDate])

  // Reload data when page or any filter changes
  useEffect(() => {
    loadCallLogs(page)
  }, [loadCallLogs, page])

  // Reset to page 1 whenever filters change
  const handleFilterChange = (setter) => (val) => {
    setter(val)
    setPage(1)
  }

  const handleResetFilters = () => {
    setSearchField('All')
    setSearchText('')
    setStatusFilter('All')
    setDirectionFilter('All')
    setProjectFilter('All')
    setAgentFilter('All')
    setFromDate('')
    setToDate('')
    setPage(1)
  }

  // Format seconds to mm:ss
  const formatDuration = (secs) => {
    if (!secs) return '00:00'
    const m = Math.floor(secs / 60)
    const s = secs % 60
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
  }

  // Toggle audio playback
  const handleToggleAudio = (logId) => {
    setPlayingAudioId((prev) => (prev === logId ? null : logId))
  }

  // CSV Export
  const handleExportCsv = () => {
    if (!callLogs.length) return
    const headers = ['ID', 'Direction', 'Status', 'Caller', 'Number', 'Agent', 'Project', 'Duration', 'Date', 'Notes', 'Recording']
    const rows = callLogs.map((l) => [
      l.id || l.call_sid,
      l.direction,
      l.call_status,
      l.caller_name || l.customer_name || '',
      l.caller_number || '',
      l.agent_name || '',
      l.project_name || '',
      formatDuration(l.duration_seconds),
      l.start_time || l.created_at || '',
      l.call_notes || '',
      l.recording_url || '',
    ])
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `call-logs-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-24">
      {/* ── TOP CANOPY HEADER ───────────────────────────────────────────── */}
      <CallLogsHeader
        totalLogsCount={totalCount}
        onRefresh={() => loadCallLogs(page)}
        loading={loading}
      />

      {/* ── KPI SUMMARY METRIC CARDS (User instructed: do not focus, ignore) ─ */}
      <CallLogsKpiCards callLogs={callLogs} loading={loading} />

      {/* ── SHARED GLOBAL FILTERS COMPONENT ───────────────────────────── */}
      <SharedFilters
        searchField={searchField}
        setSearchField={handleFilterChange(setSearchField)}
        searchFieldOptions={SEARCH_FIELD_OPTIONS}
        searchText={searchText}
        setSearchText={handleFilterChange(setSearchText)}
        statusFilter={statusFilter}
        setStatusFilter={handleFilterChange(setStatusFilter)}
        statusOptions={STATUS_OPTIONS}
        typeFilter={directionFilter}
        setTypeFilter={handleFilterChange(setDirectionFilter)}
        typeOptions={DIRECTION_OPTIONS}
        projectFilter={projectFilter}
        setProjectFilter={handleFilterChange(setProjectFilter)}
        agentFilter={agentFilter}
        setAgentFilter={handleFilterChange(setAgentFilter)}
        agentOptions={agentOptions}
        fromDate={fromDate}
        setFromDate={handleFilterChange(setFromDate)}
        toDate={toDate}
        setToDate={handleFilterChange(setToDate)}
        visibleAccordionFilters={['project', 'type', 'fromDate', 'toDate']}
        tools={[
          { key: 'export_csv', label: 'Export CSV', icon: 'download' },
        ]}
        onToolSelect={(key) => {
          if (key === 'export_csv') handleExportCsv()
        }}
        onReset={handleResetFilters}
      />

      {/* ── CALL LOGS DATA TABLE & SERVER-SIDE PAGINATION ────────────── */}
      <section className="space-y-4">
        <CallLogsTable
          filteredLogs={callLogs}
          playingAudioId={playingAudioId}
          onToggleAudio={handleToggleAudio}
          onSelectLog={(log) => setSelectedLog(log)}
          formatDuration={formatDuration}
          loading={loading}
        />

        {/* Table Footer with Pagination matching Attendance standard */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest rounded-2xl shadow-xs">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing {totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1} to{' '}
            {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount} entries
          </p>
          <AppPagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            onPrevious={() => setPage((p) => Math.max(p - 1, 1))}
            onNext={() => setPage((p) => Math.min(p + 1, totalPages))}
            canPrevious={page > 1}
            canNext={page < totalPages}
          />
        </div>
      </section>

      {/* ── CALL DETAILS SLIDE-OVER DRAWER ──────────────────────────────── */}
      <CallAuditDetailDrawer
        selectedLog={selectedLog}
        onClose={() => setSelectedLog(null)}
        formatDuration={formatDuration}
      />
    </div>
  )
}
